import { useAppContext } from 'app/useAppContext'
import { useObject } from 'data/hooks/objects'
import { publishSharedTable, unpublishSharedTable } from 'data/hooks/objects/objectOperations'
import { getIsStackerSharedObject } from 'features/admin/stackerNativeObjectUtils'

import { useToast } from 'ui/components/Toast'

type UseManageDataShareTableModalStateProps = {
    objectSid?: string
    onOpenChange: (open: boolean) => void
}

export function useManageDataShareTableModalState({
    objectSid,
    onOpenChange,
}: UseManageDataShareTableModalStateProps) {
    const { object } = useObject(objectSid)

    const isSharedTable = getIsStackerSharedObject(object)

    const titleLabel = isSharedTable
        ? 'Stop sharing table with the Workspace?'
        : 'Share table with the Workspace?'
    const subtitleLabel = isSharedTable
        ? 'Apps within this workspace will no longer have access to the data in this table.'
        : 'Apps within this workspace gain access to the data in this table.'
    const submitLabel = isSharedTable ? 'Stop sharing' : 'Share table'

    const { workspaceAccount } = useAppContext()
    const sharedResourcesStackSid = workspaceAccount?.options?.shared_resources_stack

    const toast = useToast()

    const shareTable = () => {
        if (!objectSid) return

        return publishSharedTable(
            objectSid,
            sharedResourcesStackSid,
            () => {
                onOpenChange(false)
            },
            () => {
                toast({
                    type: 'error',
                    title: 'There was a problem sharing the table',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            }
        )
    }

    const stopShareTable = () => {
        if (!objectSid) return

        return unpublishSharedTable(
            objectSid,
            () => {
                onOpenChange(false)
            },
            () => {
                toast({
                    type: 'error',
                    title: 'There was a problem updating the table',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            }
        )
    }

    const onSubmit = async () => {
        if (isSharedTable) {
            await stopShareTable()
        } else {
            await shareTable()
        }
    }

    return {
        titleLabel,
        subtitleLabel,
        submitLabel,
        onSubmit,
    }
}
